import type { SectionColor } from "../risikoanalyseStep/types";

/**
 * Section + Field-Layout für die neue Kennzahlen-Maske.
 *
 * Reihenfolge spiegelt die Sektionen im KennzahlenTab unter den Kontaktdetails:
 *  1. Umsatz gesamt
 *  2. Wareneinsatz
 *  3. Rohertrag
 *  4. Beschäftigte gesamt
 *  5. Bruttojahreslohn- und Gehaltssumme (BG-Meldung)
 *  6. Umsatzrendite
 *  7. Eigenkapital
 *  8. Verfügbare Liquidität
 *  9. Verpflichtungen
 * 10. Bürgschaften
 *
 * Die Reihenfolge erzählt die GuV von oben nach unten — deshalb sitzt der
 * Personalaufwand zwischen Rohertrag und Umsatzrendite und nicht am Ende.
 *
 * Jede Sektion hat genau ein „Gesamt"-Feld (Hauptbetrag). Es steht als erste,
 * hervorgehobene Zeile im Year-Grid und ist die Kennzahl, nach der die Sektion
 * benannt ist — die Detailfelder schlüsseln immer nur dieses eine Feld auf.
 */

export type FieldType = "euro" | "number" | "percent" | "text";

export type FieldDef = {
  /** Persistenz-Key — identisch zum KennzahlenTab-Datenmodell (Decoded). */
  key: string;
  label: string;
  type: FieldType;
  /** Einrückung in der Tabelle (0 = root, 1 = Detail, 2 = Sub-Detail). */
  indent?: 0 | 1 | 2;
  /** Optionaler Hinweistext unter dem Label. */
  hint?: string;
};

/**
 * Zusatzfragen pro Sektion (Beratermodus-Stil). Nicht jahres-übergreifend —
 * der Wert gilt für den Kontakt als Ganzes und wird im `otherInfo`-JSON
 * des aktuellen Jahres gespeichert.
 *
 * `kind`:
 *   - `yesnoText`: ja/nein-Schalter; bei "ja" erscheint ein Warum-Textfeld.
 *   - `yesnoWithFields`: ja/nein-Schalter; bei "ja" eine Reihe Zusatzfelder.
 *   - `multiText`: mehrere Text-/Zahl-Felder ohne Gate.
 */
export type BeraterQuestion =
  | {
      kind: "yesnoText";
      gateKey: string; // z.B. "umsatz_schwank_ja"
      gateLabel: string;
      warumKey: string; // z.B. "umsatz_schwank_warum"
      warumLabel?: string;
    }
  | {
      kind: "yesnoWithFields";
      gateKey: string;
      gateLabel: string;
      fields: { key: string; label: string; type: "text" | "euro" | "number" }[];
    }
  | {
      kind: "multiText";
      label: string;
      fields: { key: string; label: string; type: "text" | "euro" | "number" }[];
    };

export type SectionDef = {
  id: string;
  title: string;
  color: SectionColor;
  /** Leitkennzahl der Sektion — erste, hervorgehobene Zeile im Year-Grid. */
  gesamt: FieldDef;
  /** Aufschlüsselung der Leitkennzahl, unter ihr eingerückt. Achtung: der
   *  YearScrollGrid rendert diese Zeilen IMMER — das „Gesamt" ist die fachliche
   *  Klammer, kein technisches Gate (die Formular-Copy verspricht das Gegenteil). */
  details: FieldDef[];
  /** Zusatzfragen (nicht jahres-übergreifend), z. B. Schwankungen + Warum,
   *  Sondereffekte, Branchen-Aufteilung. Werden nach dem Year-Grid gerendert. */
  questions?: BeraterQuestion[];
  /**
   * Hängt an die Details die Umsatzverteilung je Branchengruppe an —
   * dynamisch aus den Branchen der Firma, gespeichert in `otherInfo.revBranchen`.
   */
  branchenVerteilung?: boolean;
};

export const KENNZAHLEN_SECTIONS: SectionDef[] = [
  {
    id: "umsatz",
    title: "Umsatz gesamt",
    color: "pillarOrange",
    gesamt: { key: "revenueTotal", label: "Umsatz gesamt", type: "euro" },
    // Die Branchen-Zeilen hängt die Maske dynamisch an (eine je Branchengruppe
    // der Firma, siehe `kennzahlen/revBranchen.ts`). Früher standen hier vier
    // feste Keys `revBranch…Amt`, die der Kennzahlen-Reiter nie gelesen hat.
    branchenVerteilung: true,
    details: [
      { key: "revRegionDeEur", label: "Verteilung Deutschland (€)", type: "euro", indent: 1 },
      { key: "revRegionEuEur", label: "Verteilung Europa (€)", type: "euro", indent: 1 },
      { key: "revRegionWorldEur", label: "Verteilung Weltweit (€)", type: "euro", indent: 1 },
      { key: "revRegionUsaCaEur", label: "davon USA / Kanada (€)", type: "euro", indent: 2 },
    ],
    questions: [
      {
        kind: "yesnoText",
        gateKey: "umsatz_schwank_ja",
        gateLabel: "Gibt es starke Schwankungen beim Umsatz?",
        warumKey: "umsatz_schwank_warum",
        warumLabel: "Warum?",
      },
      {
        kind: "yesnoText",
        gateKey: "sondereffekte_ja",
        gateLabel: "Gibt es Sondereffekte (z. B. Sondergeschäfte) beim Umsatz?",
        warumKey: "sondereffekte_warum",
        warumLabel: "Warum?",
      },
      {
        kind: "yesnoWithFields",
        gateKey: "innenumsaetze_ja",
        gateLabel: "Gibt es Innenumsätze?",
        fields: [{ key: "innenumsaetze_details", label: "Mit wem, wie hoch?", type: "text" }],
      },
    ],
  },
  {
    id: "wareneinsatz",
    title: "Wareneinsatz",
    color: "blue",
    gesamt: { key: "cogsEur", label: "Wareneinsatz gesamt (€)", type: "euro" },
    details: [
      { key: "cogsPct", label: "Wareneinsatzquote", type: "percent", indent: 1 },
    ],
    questions: [
      {
        kind: "yesnoText",
        gateKey: "wareneinsatzquote_schwank_ja",
        gateLabel: "Schwankt die Wareneinsatzquote?",
        warumKey: "wareneinsatzquote_warum",
        warumLabel: "Warum?",
      },
    ],
  },
  {
    id: "rohertrag",
    title: "Rohertrag",
    color: "green",
    gesamt: { key: "grossProfit", label: "Rohertrag", type: "euro" },
    details: [],
    questions: [
      {
        kind: "yesnoText",
        gateKey: "rohertrag_schwank_ja",
        gateLabel: "Gibt es Schwankungen beim Rohertrag?",
        warumKey: "rohertrag_schwank_warum",
        warumLabel: "Warum?",
      },
    ],
  },
  {
    id: "beschaeftigte",
    title: "Beschäftigte gesamt",
    color: "purple",
    // Leitkennzahl ist der Kopfbestand, nicht die Gleichzeitigkeit: „wie viele
    // Mitarbeiter haben Sie?" ist die Frage, die ein Kunde ohne Rückfrage
    // beantwortet. `avgConcurrentPersons` (Schichtbetrieb, Teilzeit-Überlappung)
    // ist eine Ableitung daraus und steht deshalb als erstes Detail darunter.
    gesamt: { key: "ma_gesamt", label: "Mitarbeiter gesamt", type: "number" },
    details: [
      { key: "avgConcurrentPersons", label: "Beschäftigte (zur gleichen Zeit)", type: "number", indent: 1 },
      { key: "ma_vollzeit", label: "Vollzeitkräfte", type: "number", indent: 1 },
      { key: "ma_monatsstunden", label: "Monatsstunden Vollzeit", type: "number", indent: 2 },
      { key: "ma_teilzeit", label: "Teilzeitkräfte", type: "number", indent: 1 },
      { key: "ma_minijobber", label: "davon Minijobber", type: "number", indent: 2 },
      { key: "ma_midijobber", label: "davon Midijobber", type: "number", indent: 2 },
    ],
  },
  // Eigene Sektion statt Detailzeilen unter „Beschäftigte": das sind Euro-Beträge
  // aus der BG-Meldung, keine Kopfzahlen — in einem Grid mit `ma_*` stünden zwei
  // Einheiten untereinander, und die Vierer-Aufschlüsselung ist zu lang, um als
  // Anhängsel einer fremden Leitkennzahl durchzugehen.
  {
    id: "bruttolohn",
    title: "Bruttojahreslohn- und Gehaltssumme (BG-Meldung)",
    // Blau wie „Wareneinsatz": beides Aufwandsblöcke der GuV. Zugleich
    // unterscheidbar von den Nachbarn (Beschäftigte lila, Rendite gold) — sonst
    // verschwimmen die Sektions-Tabs zu einem Block.
    color: "blue",
    gesamt: { key: "bg_gesamt", label: "Bruttojahreslohn- und Gehaltssumme gesamt (€)", type: "euro" },
    details: [
      { key: "bg_buero", label: "Büromitarbeiter / kaufm. Angestellte (€)", type: "euro", indent: 1 },
      { key: "bg_aussendienst", label: "Außendienst / Montage / Baustelle (€)", type: "euro", indent: 1 },
      { key: "bg_aushilfen", label: "Aushilfen, Minijobber, Praktikanten (€)", type: "euro", indent: 1 },
      { key: "bg_gf_inhaber", label: "Geschäftsführung / Inhaber (€)", type: "euro", indent: 1 },
    ],
  },
  {
    id: "rendite",
    title: "Umsatzrendite",
    color: "orange",
    gesamt: { key: "revenueMargin", label: "Umsatzrendite", type: "percent" },
    details: [],
  },
  {
    id: "eigenkapital",
    title: "Eigenkapital",
    color: "black",
    gesamt: { key: "equityEur", label: "Eigenkapital (€)", type: "euro" },
    details: [
      { key: "equityPct", label: "Eigenkapital-Quote", type: "percent", indent: 1 },
    ],
  },
  {
    id: "liquiditaet",
    title: "Verfügbare Liquidität",
    color: "pillarOrange",
    gesamt: { key: "liqTotal", label: "Liquidität gesamt (€)", type: "euro" },
    details: [
      { key: "liqShort", label: "Kurzfristig (€)", type: "euro", indent: 1 },
      { key: "liqMid", label: "Mittelfristig (€)", type: "euro", indent: 1 },
      { key: "liqLong", label: "Langfristig (€)", type: "euro", indent: 1 },
    ],
  },
  {
    id: "verpflichtungen",
    title: "Verpflichtungen",
    color: "blue",
    gesamt: { key: "liabTotal", label: "Verpflichtungen gesamt (€)", type: "euro" },
    details: [
      { key: "liabKK", label: "Kontokorrent-Rahmen (€)", type: "euro", indent: 1 },
      { key: "liabLoan", label: "Kredite / Darlehen (€)", type: "euro", indent: 1 },
      { key: "liabOther", label: "Sonstige Verpflichtungen (€)", type: "euro", indent: 1 },
    ],
  },
  {
    id: "buergschaften",
    title: "Bürgschaften",
    color: "green",
    gesamt: { key: "guaranteeTotal", label: "Bürgschaften insgesamt (€)", type: "euro" },
    details: [
      { key: "guaranteeAvg", label: "Im Schnitt (€)", type: "euro", indent: 1 },
    ],
  },
];

/**
 * Felder, die direkt auf das `KPIYear`-Topfeld abgebildet werden. Andere Keys
 * landen im `otherInfo`-JSON-Bag.
 *
 * Nicht deckungsgleich mit den `gesamt`-Feldern der Sektionen, auch wenn es lange
 * so aussah: `avgConcurrentPersons` ist seit dem Katalog-Umbau ein Detail unter
 * „Beschäftigte", bleibt aber eine eigene Spalte in `ContactKpi`. Diese Menge
 * deshalb nie aus `sec.gesamt` ableiten — das verschöbe den Wert nach `otherInfo`
 * und der Kennzahlen-Reiter läse ab dann eine leere Spalte.
 */
export const TOPLEVEL_FIELD_KEYS = new Set([
  "revenueTotal",
  "cogsEur",
  "grossProfit",
  "avgConcurrentPersons",
]);
